import { useEffect, useState } from 'react'
import type { Provider } from 'app/generated/graphql'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { log, logError } from 'lib/logging'

const PROVIDER_FIELDS = `id vendorId displayName resourceName kollaConsumerId`
const PROVIDER_LIMIT = 500

export type ProviderNames = {
  names: Map<string, string> // vendorId -> display name
  loading: boolean
}

// Resolves PMS provider vendorIds to display names for a Kolla location via the
// GraphQL `providers` model, keyed by vendorId (which matches Appointment.providerId).
// The legacy REST /api/providers/name endpoint 404s for these ids, and per AGENTS.md
// GraphQL is preferred over the deprecated REST surface.
export function useProviderNames(kollaConsumerId?: string | null): ProviderNames {
  const [state, setState] = useState<ProviderNames>({ names: new Map(), loading: !!kollaConsumerId })

  useEffect(() => {
    if (!kollaConsumerId) {
      setState({ names: new Map(), loading: false })
      return
    }
    let cancelled = false
    setState((s) => ({ ...s, loading: true }))
    // Scope to this location server-side — vendorIds (e.g. "provider_3") can repeat
    // across a workspace's locations, and fetching every provider just to filter
    // client-side is wasteful for large multi-location orgs.
    const search = `kollaConsumerId:${kollaConsumerId}`
    gqlFetchList<Provider>('providers', { search, pagination: { limit: PROVIDER_LIMIT } }, PROVIDER_FIELDS)
      .then(({ hits }) => {
        if (cancelled) return
        if (hits.length >= PROVIDER_LIMIT) {
          log(
            `useProviderNames`,
            `provider list hit the ${PROVIDER_LIMIT} cap; some names may be missing`,
            kollaConsumerId,
          )
        }
        const names = new Map<string, string>()
        for (const p of hits) {
          const name = p.displayName || p.resourceName
          if (p.vendorId && name) names.set(p.vendorId, name)
        }
        setState({ names, loading: false })
      })
      .catch((e) => {
        if (cancelled) return
        logError(`useProviderNames: failed to fetch providers`, e)
        setState({ names: new Map(), loading: false })
      })
    return () => {
      cancelled = true
    }
  }, [kollaConsumerId])

  return state
}
